import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star } from "lucide-react";
import PdfPreview from "./PdfPreview";
import { Reveal } from "./Reveal";

const METRICS = [
  { v: "10.000+", l: "orçamentos gerados" },
  { v: "11s", l: "tempo médio" },
  { v: "4,9/5", l: "avaliação média" },
  { v: "30+", l: "nichos atendidos" },
];

const NICHES = [
  { key: "obras", label: "Obras & Reformas", pdf: { brand: "Construtora RS", title: "Reforma de apartamento", client: "Ricardo Silva", accent: "#b45309", items: [{ d: "Mão de obra", q: 1, v: 8500 }, { d: "Materiais", q: 1, v: 6200 }, { d: "Pintura", q: 1, v: 3800 }] } },
  { key: "arq", label: "Arquitetura", pdf: { brand: "Mendes Interiores", title: "Projeto de interiores", client: "Beatriz Mendes", accent: "#0f172a", items: [{ d: "Projeto executivo", q: 1, v: 7000 }, { d: "Detalhamento", q: 1, v: 3500 }, { d: "Acompanhamento", q: 1, v: 2500 }] } },
  { key: "tech", label: "Tecnologia", pdf: { brand: "Alencar Dev", title: "App mobile sob demanda", client: "Loja Vibe", accent: "#4A3BFA", items: [{ d: "UX/UI Design", q: 1, v: 6000 }, { d: "Desenvolvimento", q: 1, v: 12000 }, { d: "Publicação nas lojas", q: 1, v: 1800 }] } },
  { key: "foto", label: "Fotografia", pdf: { brand: "Studio Carlos", title: "Cobertura de casamento", client: "Família Duarte", accent: "#7c3aed", items: [{ d: "Ensaio pré-wedding", q: 1, v: 900 }, { d: "Cobertura completa", q: 1, v: 2300 }, { d: "Álbum premium", q: 1, v: 700 }] } },
];

const TESTIMONIALS = [
  { url: "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGF2YXRhciUyMHBvcnRyYWl0fGVufDB8fHx8MTc4ODgyMDY5Mnww&ixlib=rb-4.1.0&q=85", name: "Ricardo Silva", role: "Engenheiro · Obras", quote: "Mandava orçamento em bloco de notas. Hoje faço no WhatsApp em 10 segundos e fecho mais contrato." },
  { url: "https://images.unsplash.com/photo-1607746882042-944635dfe10e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGF2YXRhciUyMHBvcnRyYWl0fGVufDB8fHx8MTc4ODgyMDY5Mnww&ixlib=rb-4.1.0&q=85", name: "Beatriz Mendes", role: "Designer de Interiores", quote: "Meus orçamentos ficaram com a cara do estúdio. Cliente elogia o profissionalismo antes de fechar." },
  { url: "https://images.unsplash.com/photo-1676989880361-091e12efc056?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGF2YXRhciUyMHBvcnRyYWl0fGVufDB8fHx8MTc4ODgyMDY5Mnww&ixlib=rb-4.1.0&q=85", name: "Lucas Alencar", role: "Dev Fullstack", quote: "Meus orçamentos saem em segundos e com um acabamento impecável. Fecho mais rápido do que nunca." },
];

export default function SocialProof({ onCta }) {
  const [niche, setNiche] = useState("obras");
  const current = NICHES.find((n) => n.key === niche);
  return (
    <section id="prova-social" className="relative px-5 py-20 sm:py-28 bg-white border-y border-slate-200/70">
      <div className="max-w-6xl mx-auto">
        {/* metrics */}
        <Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {METRICS.map((m) => (
              <div key={m.l} className="text-center rounded-2xl bg-slate-50 ring-1 ring-slate-100 py-6">
                <div className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{m.v}</div>
                <div className="text-[13px] text-slate-500 mt-1">{m.l}</div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* niche examples */}
        <div className="mt-16 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-600 mb-4">Serve para qualquer nicho</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Feito para o seu tipo de trabalho</h2>
            <p className="mt-4 text-slate-600 text-base sm:text-lg">Obras, design, tecnologia, fotografia, consultoria, eventos e muito mais. Escolha um nicho e veja um exemplo real.</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {NICHES.map((n) => (
                <button key={n.key} onClick={() => setNiche(n.key)} data-testid={`niche-${n.key}`}
                  className={`text-[13px] font-medium px-4 py-2 rounded-full transition-all ${niche === n.key ? "bg-slate-900 text-white" : "bg-white ring-1 ring-slate-200 text-slate-600 hover:ring-slate-300"}`}>
                  {n.label}
                </button>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mx-auto max-w-[320px]">
              <AnimatePresence mode="wait">
                <motion.div key={niche} initial={{ opacity: 0, y: 16, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -16, scale: 0.97 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
                  <PdfPreview {...current.pdf} />
                </motion.div>
              </AnimatePresence>
            </div>
          </Reveal>
        </div>

        {/* testimonials */}
        <div className="mt-20 grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08}>
              <div className="h-full rounded-3xl bg-slate-50 ring-1 ring-slate-100 p-6 flex flex-col">
                <div className="flex items-center gap-0.5 mb-3">{[0, 1, 2, 3, 4].map((s) => <Star key={s} size={15} className="fill-amber-400 text-amber-400" />)}</div>
                <p className="text-[14px] text-slate-700 leading-relaxed flex-1">“{t.quote}”</p>
                <div className="mt-5 flex items-center gap-3">
                  <img src={t.url} alt={t.name} referrerPolicy="no-referrer" className="w-10 h-10 rounded-full object-cover ring-1 ring-slate-200" />
                  <div><div className="text-[13px] font-semibold text-slate-900">{t.name}</div><div className="text-[12px] text-slate-400">{t.role}</div></div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
